/* eslint-disable react/prop-types */
import { useMergeRefs } from '@floating-ui/react';
import { AuthorizationWrapper, Permission } from '@frontend/authorization';
import { ClassType } from '@frontend/common';
import React from 'react';
import { IconType } from 'react-icons';

import useDropdownItem from './dropdown-item.controller';

const ID = 'dropdown-item';
export interface DropdownItemProps {
    'data-testid'?: string;
    id?: string;
    label: React.ReactNode;
    disabled?: boolean;
    classType?: ClassType;
    icon?: IconType;
    requiredPermissions?: Permission[];
    hide?: boolean | (() => boolean);
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
}

export const DropdownItem = React.forwardRef<HTMLButtonElement, DropdownItemProps & React.ButtonHTMLAttributes<HTMLButtonElement>>(function item(
    { label, disabled, ...props },
    forwardedRef
) {
    const viewProps = useDropdownItem();
    if (typeof props.hide === 'function' ? props.hide() : props.hide) {
        return null;
    }
    const color: Record<string, string> = {
        [ClassType.PRIMARY]: 'text-brand-primary',
        [ClassType.SECONDARY]: 'text-brand-secondary',
        [ClassType.SUCCESS]: 'text-status-success',
        [ClassType.WARNING]: 'text-status-warning',
        [ClassType.DANGER]: 'text-status-danger',
        [ClassType.INFO]: 'text-status-info',
        [ClassType.LIGHT]: 'text-status-light',
        [ClassType.DARK]: 'text-status-dark',
        [ClassType.ALERT]: 'text-status-alert',
        [ClassType.DISABLED]: 'text-status-disabled'
    };
    if (props.requiredPermissions) {
        return (
            <AuthorizationWrapper requiredPermissions={props.requiredPermissions}>
                <button
                    {...props}
                    ref={useMergeRefs([viewProps.item.ref, forwardedRef])}
                    id={props.id ?? ID}
                    type='button'
                    role='menuitem'
                    className='hover:bg-brand-light flex cursor-pointer items-center focus:outline-none'
                    tabIndex={viewProps.isActive ? 0 : -1}
                    disabled={disabled}
                    data-testid={props['data-testid'] ?? ID}
                    {...viewProps.menu.getItemProps({
                        onClick(event: React.MouseEvent<HTMLButtonElement>) {
                            props.onClick?.(event);
                            viewProps.tree?.events.emit('click');
                        },
                        onFocus(event: React.FocusEvent<HTMLButtonElement>) {
                            props.onFocus?.(event);
                            viewProps.menu.setHasFocusInside(true);
                        }
                    })}>
                    <span className={`flex w-full flex-row items-center justify-start px-3 py-1 ${viewProps.isActive && `bg-border-default`}`}>
                        <span className='btn-inner--icon'>
                            {props.icon && <props.icon className={`${props.classType ? color[props.classType] : 'text-brand-primary'} me-2`} />}
                        </span>
                        <span className={`${props.className ?? ''} text-foreground-default`}>{label}</span>
                    </span>
                </button>
            </AuthorizationWrapper>
        );
    }

    return (
        <button
            {...props}
            ref={useMergeRefs([viewProps.item.ref, forwardedRef])}
            id={ID}
            type='button'
            role='menuitem'
            data-testid={props['data-testid'] ?? ID}
            className='hover:bg-brand-light flex cursor-pointer items-center focus:outline-none'
            tabIndex={viewProps.isActive ? 0 : -1}
            disabled={disabled}
            {...viewProps.menu.getItemProps({
                onClick(event: React.MouseEvent<HTMLButtonElement>) {
                    props.onClick?.(event);
                    viewProps.tree?.events.emit('click');
                },
                onFocus(event: React.FocusEvent<HTMLButtonElement>) {
                    props.onFocus?.(event);
                    viewProps.menu.setHasFocusInside(true);
                }
            })}>
            <span className={`flex w-full flex-row items-center justify-start px-3 py-1 ${viewProps.isActive && `bg-border-default`}`}>
                <span className='btn-inner--icon'>
                    {props.icon && <props.icon className={`${props.classType ? color[props.classType] : 'text-brand-primary'} me-2`} />}
                </span>
                <span className={`${props.className ?? ''} text-foreground-default`}>{label}</span>
            </span>
        </button>
    );
});

export default DropdownItem;
